/**
 * PublicAuthContext – Auth0-basierter Kontext für öffentliche User-Features.
 * (Hype, Votes, Profil, Charts etc.)
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import i18n from "../i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PublicAuthContext = createContext({
  publicUser: null,
  token: null,
  publicApi: null,
  login: () => {},
  logoutPublic: () => {},
  setPublicUser: () => {},
  showOnboarding: false,
  setShowOnboarding: () => {},
  isPublicAuth: false,
  isPublicLoading: true,
  interactionCache: {},
  updateInteractionCache: () => {},
});

export function PublicAuthProvider({ children }) {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout, loginWithRedirect, user: auth0User } = useAuth0();
  const [publicUser, setPublicUser] = useState(null);
  const [token, setToken] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [interactionCache, setInteractionCache] = useState({}); // releaseId → { voted, voteCount, hyped, hypeCount, remaining }

  const updateInteractionCache = useCallback((releaseId, data) => {
    setInteractionCache(prev => ({
      ...prev,
      [releaseId]: { ...(prev[releaseId] || {}), ...data },
    }));
  }, []);

  const getToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE || "https://api.huwnd.de",
        },
      });
    } catch {
      return null;
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      setPublicUser(null);
      setToken(null);
      return;
    }

    let cancelled = false;
    const syncUser = async () => {
      const t = await getToken();
      if (!t || cancelled) return;
      setToken(t);
      try {
        // Name/Email aus ID-Token (auth0User) als Fallback mitsenden
        const syncRes = await axios.post(
          `${BACKEND_URL}/api/me/sync`,
          {
            name: auth0User?.name || auth0User?.nickname,
            email: auth0User?.email,
            picture: auth0User?.picture,
          },
          { headers: { Authorization: `Bearer ${t}` } }
        );
        // GET /me/me nach Sync für vollständige Daten (inkl. artist_slugs)
        const meRes = await axios.get(
          `${BACKEND_URL}/api/me/me`,
          { headers: { Authorization: `Bearer ${t}` } }
        );
        if (!cancelled) {
          setPublicUser(meRes.data);
          if (meRes.data?.needs_onboarding || syncRes.data?.needs_onboarding) {
            setShowOnboarding(true);
          }
          // DB-Sprache beim Login wiederherstellen (geräteübergreifend)
          if (meRes.data?.preferred_language) {
            i18n.changeLanguage(meRes.data.preferred_language);
          }
        }
      } catch {
        if (!cancelled) setPublicUser(null);
      }
    };

    syncUser();
    return () => { cancelled = true; };
  }, [isAuthenticated, isLoading, getToken]);

  const publicApi = useCallback(
    async (method, url, data = null) => {
      const t = token || (await getToken());
      return axios({
        method,
        url: `${BACKEND_URL}/api${url}`,
        data,
        headers: { Authorization: `Bearer ${t}` },
      });
    },
    [token, getToken]
  );

  const login = useCallback((options = {}) => {
    loginWithRedirect({
      appState: options?.appState || { returnTo: "/" },
    });
  }, [loginWithRedirect]);

  const logoutPublic = () => {
    setPublicUser(null);
    setToken(null);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <PublicAuthContext.Provider
      value={{ publicUser, setPublicUser, token, publicApi, login, logoutPublic, showOnboarding, setShowOnboarding, isPublicAuth: isAuthenticated, isPublicLoading: isLoading, interactionCache, updateInteractionCache }}
    >
      {children}
    </PublicAuthContext.Provider>
  );
}

export function usePublicAuth() {
  return useContext(PublicAuthContext);
}
